import type { AbhaSessionState } from '@helpers/abhaJourneyHelper'

export const ABHA_ANALYTICS_PRODUCT = 'ABHA'

export const ABHA_ANALYTICS_EVENTS = {
  VIEW_PAGE: 'view_page',
  APP_VIEW_PAGE: 'app_view_page',
  VIEW_AADHAAR_ABHA_PAGE: 'view_aadhaar_abha_page',
  TAP_BTN_CTA_WIDGET: 'tap_btn_CTA_widget',
  TAP_BTN_ENGAGE: 'tap_btn_engage',
  TAP_BTN_ACKO_APP: 'tap_btn_acko_app',
  TAP_AADHAAR_NUMBER_SUBMIT: 'tap_aadhaar_number_submit',
  TAP_AADHAAR_NUMBER_OTP_SUBMIT: 'tap_aadhaar_number_otp_submit',
} as const

export const ABHA_STATE = {
  VIEW_ABHA: 'view_abha',
  DOWNLOAD_ABHA: 'download_abha',
  POST_VIEW_ABHA_SCREEN: 'post_view_abha_screen',
  SINGLE_CARD: 'single_card',
} as const

export const ABHA_ANALYTICS_VARIANTS = {
  NO_ABHA_FOUND: 'no_abha_found',
} as const

export type AbhaAnalyticsContext = 'widget' | 'new_abha_create'

export const ABHA_ANALYTICS_JOURNEYS = {
  LANDING: 'ABHA',
  PHONE_NUMBER_ENTRY: 'phone_number_entry',
  NEW_ABHA_ENTRY: 'new_abha_entry',
  EXISTING_ABHA_ENTRY: 'existing_abha_entry',
  SEND_OTP: 'aadhaar_phone_entered',
  VERIFY_OTP_SCREEN: 'verify_otp_screen',
  APP_DOWNLOAD_BANNER: 'app_download_banner',
  APP_DOWNLOAD_CLICK: 'app_download_click',
  APP_DOWNLOAD_PAGE: 'app_download_page',
  VIEW_ABHA_SCREEN: 'view_abha_screen',
  CREATE_ABHA: 'create_abha',
} as const

export const ABHA_ANALYTICS_PAGE_TYPES = {
  AADHAAR_PHONE_ENTRY: 'aadhaar_phone_entry',
  AADHAAR_PHONE_ENTERED: 'aadhaar_phone_entered',
  AADHAAR_VERIFICATION_SCREEN: 'aadhaar_verification_screen',
  ENTER_PHONE_NUMBER_PAGE: 'enter_phone_number_page',
  PHONE_VERIFICATION_SCREEN: 'phone_verification_screen',
  PHONE_OTP_SCREEN: 'phone_otp_screen',
  AADHAAR_OTP_SCREEN: 'aadhaar_otp_screen',
  AADHAAR_CONSENT: 'aadhaar_consent',
} as const

export const ABHA_ANALYTICS_ACTION_TYPES = {
  ABHA_CARD_DOWNLOAD: 'abha_card_download',
  CREATE_ANOTHER_ABHA: 'create_another_abha',
  PHONE_NUMBER_ENTERED: 'phone_number_entered',
  PHONE_NUM_OTP_SUBMIT: 'phone_number_otp_submit',
  AADHAAR_OTP_SUBMIT: 'aadhaar_otp_submit',
  PHONE_OTP_SUBMIT: 'phone_otp_submit',
  RESEND_OTP: 'resend_otp',
  REDIRECTION: 'redirection',
  VIEW_ABHA_CTA_CLICK: 'View_abha_cta_click',
  PROFILE: 'profile',
} as const

export type AbhaSuccessAnalyticsVariant = 'emergency_ready' | 'hospital_bill' | 'abha_number_hidden'

type SuccessStepAnalyticsData = {
  cardVariant?: string
  variantName?: AbhaSuccessAnalyticsVariant
  desktopData?: {
    content?: Array<{
      type?: string
      ctaText?: string
      items?: Array<{ text?: string }>
    }>
  }
  mobileData?: {
    content?: Array<{
      type?: string
      ctaText?: string
      items?: Array<{ text?: string }>
    }>
  }
}

type SuccessContentBlock = {
  type?: string
  ctaText?: string
  items?: Array<{ text?: string }>
}

const hasEmergencyFeatures = (content?: SuccessContentBlock[]) =>
  content?.some(
    block => block?.type === 'features' && block.items?.some(item => /ambulance|emergency/i.test(item.text ?? ''))
  ) ?? false

const hasHealthPromoQrCard = (content?: SuccessContentBlock[]) =>
  content?.some(block => block?.type === 'qr_card' && Boolean(block.ctaText)) ?? false

export const resolveAbhaSuccessVariantName = (
  successStepData?: SuccessStepAnalyticsData
): AbhaSuccessAnalyticsVariant | undefined => {
  if (successStepData?.variantName) {
    return successStepData.variantName
  }

  if (successStepData?.cardVariant === 'hideAbhaNumber') {
    return 'abha_number_hidden'
  }

  const panelData = successStepData?.desktopData ?? successStepData?.mobileData
  const content = panelData?.content ?? []

  if (hasEmergencyFeatures(content)) {
    return 'emergency_ready'
  }

  if (hasHealthPromoQrCard(content)) {
    return 'hospital_bill'
  }

  return undefined
}

export const buildAbhaAnalyticsProps = (props: Record<string, unknown> = {}) => ({
  product: ABHA_ANALYTICS_PRODUCT,
  ...props,
})

export const buildAbhaPageViewProps = (journey: string, extra: Record<string, unknown> = {}) =>
  buildAbhaAnalyticsProps({
    journey,
    ...extra,
  })

/** @deprecated Use buildAbhaAnalyticsProps — kept for any legacy call sites during migration */
export const buildAbhaHeroAnalyticsProps = ({
  phone,
  session,
  segmentEventCommonProperties = {},
}: {
  phone: string
  session?: AbhaSessionState | null
  segmentEventCommonProperties?: Record<string, unknown>
}) => {
  const memberId = session?.abha_member_list_details?.[0]?.id

  return buildAbhaAnalyticsProps({
    ...segmentEventCommonProperties,
    communication_phone: phone,
    ...(memberId ? { unique_id: String(memberId) } : {}),
  })
}

/** @deprecated Use ABHA_ANALYTICS_EVENTS + buildAbhaPageViewProps */
export const ABHA_HERO_ANALYTICS_EVENTS = {
  VIEW_AADHAAR_PAGE: ABHA_ANALYTICS_EVENTS.VIEW_PAGE,
  TAP_AADHAAR_SUBMIT: ABHA_ANALYTICS_EVENTS.TAP_BTN_CTA_WIDGET,
  TAP_OTP_SUBMIT: ABHA_ANALYTICS_EVENTS.TAP_BTN_ENGAGE,
  VIEW_ABHA_CARD_PAGE: ABHA_ANALYTICS_EVENTS.VIEW_PAGE,
} as const
